/**
 * כרטיס לקוח — the entity-detail-with-tabs archetype.
 *
 * Identity header with the key facts, then tabbed panels. One request fills all of it:
 * the API returns cadences, compliance history, open cases, documents and the money
 * summary together, because six round trips to render one screen shows up as a staggered
 * load on every client the user opens.
 *
 * The money and profitability tabs are absent for roles that may not see them, not
 * disabled. The API omits those fields entirely, so there is nothing to render.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AUTHORITIES,
  directionHintFor,
  formatDate,
  formatIsraeliPhone,
  formatMonthName,
  type AuthorityKey,
} from "@onecpa/shared";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Fact,
  Field,
  Input,
  Money,
  PageHeader,
  Panel,
  ProfitabilityBar,
  StatusBadge,
  TabStrip,
  TableSkeleton,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { ClientPeople } from "./ClientPeople.js";
import { agorot, minutesToLabel } from "../app/hooks.js";
import { useRole, useSession } from "../app/session.js";

type TabId =
  "overview" | "people" | "compliance" | "cases" | "documents" | "money" | "profitability";

export function ClientCard() {
  const { clientId } = useParams({ from: "/shell/clients/$clientId" });
  const session = useSession();
  const { isOwner, isManager } = useRole();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabId>("overview");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["client", session.orgId, clientId],
    queryFn: () => api.clients.get(session.orgId, clientId),
  });

  const startTimer = useMutation({
    mutationFn: () =>
      api.time.start(session.orgId, {
        clientId,
        description: `עבודה עבור ${data?.client.name ?? ""}`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["time"] });
      session.toast("הטיימר הופעל");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const remove = useMutation({
    mutationFn: () => api.clients.remove(session.orgId, clientId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      session.toast("הלקוח הועבר לנמחקים");
      await navigate({ to: "/clients" });
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="p-5">
        <TableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  const { client } = data;

  const tabs = [
    { id: "overview", label: "סקירה" },
    { id: "people", label: "אנשי קשר ותיקים" },
    { id: "compliance", label: "דיווחים", count: data.compliance.length },
    { id: "cases", label: "תיקים", count: data.annual.length + data.wealth.length },
    { id: "documents", label: "מסמכים", count: data.files.length },
    ...(data.money ? [{ id: "money", label: "גבייה", count: data.money.invoices.length }] : []),
    ...(data.profitability ? [{ id: "profitability", label: "רווחיות" }] : []),
  ];

  return (
    <>
      <PageHeader
        title={client.name}
        subtitle={client.clientType === "company" ? "חברה בע״מ" : "עוסק מורשה"}
        period={client.isDeleted ? "נמחק" : undefined}
        action={
          <div className="flex items-center gap-2">
            <Button icon="timer" onClick={() => startTimer.mutate()} loading={startTimer.isPending}>
              התחל טיימר
            </Button>
            {isManager && (
              <Button onClick={() => setEditing(true)} icon="settings">
                עריכה
              </Button>
            )}
          </div>
        }
      >
        <TabStrip tabs={tabs} active={tab} onSelect={(id) => setTab(id as TabId)} />
      </PageHeader>

      <div className="p-5">
        {tab === "overview" && (
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="פרטי זיהוי" className="lg:col-span-2">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 px-3.5 py-3 md:grid-cols-3">
                <Fact label="מספר עוסק / ח״פ">
                  <bdi dir={directionHintFor("tax_file_number")}>{client.taxId ?? "—"}</bdi>
                </Fact>
                <Fact label="תיק מס הכנסה">
                  <bdi dir="ltr">{client.incomeTaxFileNumber ?? "—"}</bdi>
                </Fact>
                <Fact label="תיק מע״מ">
                  <bdi dir="ltr">{client.vatFileNumber ?? "—"}</bdi>
                </Fact>
                <Fact label="תיק ניכויים">
                  <bdi dir="ltr">{client.withholdingFileNumber ?? "—"}</bdi>
                </Fact>
                <Fact label="דוא״ל">
                  <bdi dir="ltr">{client.email ?? "—"}</bdi>
                </Fact>
                <Fact label="טלפון">
                  <bdi className="tnum">
                    {client.phoneE164 ? formatIsraeliPhone(client.phoneE164) : "—"}
                  </bdi>
                </Fact>
                <Fact label="עיר">{client.city ?? "—"}</Fact>
                <Fact label="מנהל חשבונות">{client.assignedStaffName ?? "לא מוקצה"}</Fact>
                <Fact label="מעסיק עובדים">{client.employsWorkers ? "כן" : "לא"}</Fact>
                <Fact label='מע"מ מפורט (PCN874)'>{client.vatIsDetailed ? "כן" : "לא"}</Fact>
                {isManager && (
                  <Fact label="ריטיינר חודשי">
                    {client.monthlyFee ? (
                      <Money agorot={agorot(client.monthlyFee)} colorize={false} />
                    ) : (
                      "לא הוגדר"
                    )}
                  </Fact>
                )}
                <Fact label="מספר תיק פנימי">
                  <bdi dir="ltr">{client.internalFileNumber ?? "—"}</bdi>
                </Fact>
                <Fact label="נוצר">
                  <bdi className="tnum">{formatDate(client.createdAt)}</bdi>
                </Fact>
              </dl>
              {client.notes && (
                <p className="border-t border-line-soft px-3.5 py-2.5 text-xs text-ink-muted">
                  {client.notes}
                </p>
              )}
            </Panel>

            <Panel title="תדירויות דיווח">
              <ul className="divide-y divide-line-soft">
                {data.frequencies.map((frequency) => (
                  <li key={frequency.authority} className="flex items-center gap-2 px-3.5 py-2">
                    <span className="flex-1 text-xs text-ink">
                      {AUTHORITIES[frequency.authority as AuthorityKey]?.label ??
                        frequency.authority}
                    </span>
                    <Badge tone={frequency.frequency === "not_applicable" ? "neutral" : "info"}>
                      {FREQUENCY_LABELS[frequency.frequency] ?? frequency.frequency}
                    </Badge>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title="משימות פתוחות" className="lg:col-span-2">
              {data.tasks.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {data.tasks.map((task) => (
                    <li key={task.id} className="flex items-center gap-3 px-3.5 py-2">
                      <span className="min-w-0 flex-1 truncate text-xs text-ink">
                        {task.title}
                      </span>
                      {task.dueDate && (
                        <bdi className="tnum text-2xs text-ink-faint">
                          {formatDate(task.dueDate)}
                        </bdi>
                      )}
                      <StatusBadge vocabulary="task" status={task.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="אין משימות פתוחות" icon="check" />
              )}
            </Panel>

            {isManager && (
              <Panel title="פעולות">
                <div className="flex flex-col gap-2 px-3.5 py-3">
                  {client.isDeleted ? (
                    <Button
                      onClick={async () => {
                        await api.clients.restore(session.orgId, clientId);
                        await queryClient.invalidateQueries({ queryKey: ["client"] });
                        session.toast("הלקוח שוחזר");
                      }}
                    >
                      שחזור לקוח
                    </Button>
                  ) : (
                    <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                      מחיקת לקוח
                    </Button>
                  )}
                  <p className="text-2xs text-ink-faint">
                    מחיקה היא רכה: הלקוח נשמר וניתן לשחזר אותו, וההיסטוריה נשארת ביומן הביקורת.
                  </p>
                </div>
              </Panel>
            )}
          </div>
        )}

        {tab === "people" && <ClientPeople clientId={clientId} />}

        {tab === "compliance" && (
          <Panel title="היסטוריית דיווחים">
            {data.compliance.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {data.compliance.map((row) => (
                  <li key={row.id} className="flex items-center gap-3 px-3.5 py-2">
                    <span className="w-28 text-xs text-ink">
                      {formatMonthName(row.periodMonth)} {row.periodYear}
                    </span>
                    <span className="flex-1 text-xs text-ink-muted">
                      {AUTHORITIES[row.authority as AuthorityKey]?.label ?? row.authority}
                    </span>
                    <bdi className="tnum hidden text-2xs text-ink-faint sm:block">
                      {formatDate(row.dueDate)}
                    </bdi>
                    {row.penaltyAmount && <Money agorot={agorot(row.penaltyAmount)} />}
                    <StatusBadge vocabulary="monthly" status={row.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין היסטוריית דיווחים" />
            )}
          </Panel>
        )}

        {tab === "cases" && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="דוחות שנתיים">
              {data.annual.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {data.annual.map((row) => (
                    <li key={row.id} className="flex items-center gap-3 px-3.5 py-2">
                      <Link
                        to="/annual/$caseId"
                        params={{ caseId: row.id }}
                        className="link-underline flex-1 text-xs font-semibold text-ink"
                      >
                        שנת מס {row.taxYear}
                      </Link>
                      {row.dueDate && (
                        <bdi className="tnum text-2xs text-ink-faint">
                          {formatDate(row.dueDate)}
                        </bdi>
                      )}
                      <StatusBadge vocabulary="workspace" status={row.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="אין דוחות שנתיים" />
              )}
            </Panel>

            <Panel title="הצהרות הון">
              {data.wealth.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {data.wealth.map((row) => (
                    <li key={row.id} className="flex items-center gap-3 px-3.5 py-2">
                      <Link
                        to="/wealth/$caseId"
                        params={{ caseId: row.id }}
                        className="link-underline flex-1 text-xs font-semibold text-ink"
                      >
                        לתאריך {formatDate(row.declarationDate)}
                      </Link>
                      <StatusBadge vocabulary="workspace" status={row.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="אין הצהרות הון" />
              )}
            </Panel>
          </div>
        )}

        {tab === "documents" && (
          <Panel title="מסמכים">
            {data.files.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {data.files.map((file) => (
                  <li key={file.id} className="flex items-center gap-3 px-3.5 py-2">
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">
                      {file.displayName}
                    </span>
                    <span className="tnum hidden text-2xs text-ink-faint sm:block">
                      {Math.round(file.sizeBytes / 1024)} KB
                    </span>
                    {file.clientVisible && <Badge tone="info">גלוי ללקוח</Badge>}
                    <StatusBadge vocabulary="scan" status={file.scanStatus} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין מסמכים" />
            )}
          </Panel>
        )}

        {tab === "money" && data.money && (
          <Panel title="חשבונות">
            {data.money.invoices.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {data.money.invoices.map((invoice) => (
                  <li key={invoice.id} className="flex items-center gap-3 px-3.5 py-2">
                    <span className="tnum w-16 text-xs text-ink">
                      {invoice.number ? `#${invoice.number}` : "טיוטה"}
                    </span>
                    <span className="flex-1 text-xs text-ink-muted">
                      {invoice.periodMonth
                        ? `${formatMonthName(invoice.periodMonth)} ${invoice.periodYear}`
                        : "—"}
                    </span>
                    <Money agorot={agorot(invoice.total)} colorize={false} />
                    <StatusBadge vocabulary="invoice" status={invoice.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין חשבונות" />
            )}
          </Panel>
        )}

        {tab === "profitability" && data.profitability && isOwner && (
          <Panel title="רווחיות לפי חודש">
            <ul className="divide-y divide-line-soft">
              {data.profitability.map((row) => {
                const margin = agorot(row.marginAmount);
                const maxMagnitude = Math.max(
                  ...data.profitability!.map((entry) => Math.abs(agorot(entry.marginAmount))),
                  1,
                );
                return (
                  <li
                    key={`${row.periodYear}-${row.periodMonth}`}
                    className="flex items-center gap-3 px-3.5 py-2"
                  >
                    <span className="w-24 text-xs text-ink">
                      {formatMonthName(row.periodMonth)} {row.periodYear}
                    </span>
                    <span className="tnum w-20 text-2xs text-ink-faint">
                      {minutesToLabel(row.minutesLogged)}
                    </span>
                    <span className="flex-1">
                      {row.band === "undefined_band" ? (
                        <span className="text-xs text-ink-faint">אין ריטיינר להשוואה</span>
                      ) : (
                        <ProfitabilityBar marginAgorot={margin} maxAgorot={maxMagnitude} />
                      )}
                    </span>
                    <StatusBadge vocabulary="profitability" status={row.band} />
                  </li>
                );
              })}
            </ul>
            <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
              סוגריים = הפסד · הרווחיות מחושבת כריטיינר פחות שעות שנרשמו כפול עלות שעה
            </p>
          </Panel>
        )}
      </div>

      <EditClientDialog
        open={editing}
        onClose={() => setEditing(false)}
        clientId={clientId}
        initial={client}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        title="מחיקת לקוח"
        message={`הלקוח ${client.name} יועבר לנמחקים. ניתן לשחזר אותו בכל עת.`}
        confirmLabel="מחיקה"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: "חודשי",
  bi_monthly: "דו-חודשי",
  semi_annual: "חצי שנתי",
  annual: "שנתי",
  not_applicable: "לא רלוונטי",
};

function EditClientDialog({
  open,
  onClose,
  clientId,
  initial,
}: {
  open: boolean;
  onClose(): void;
  clientId: string;
  initial: {
    name: string;
    email: string | null;
    phoneE164: string | null;
    city: string | null;
    monthlyFee: string | null;
  };
}) {
  const session = useSession();
  const { isManager } = useRole();
  const queryClient = useQueryClient();

  const [name, setName] = useState(initial.name);
  const [email, setEmail] = useState(initial.email ?? "");
  const [phone, setPhone] = useState(initial.phoneE164 ?? "");
  const [city, setCity] = useState(initial.city ?? "");
  const [fee, setFee] = useState(initial.monthlyFee ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.clients.update(session.orgId, clientId, {
        name: name.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phoneE164: phone.trim() } : {}),
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(isManager && fee.trim() ? { monthlyFee: Number(fee) } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      session.toast("הפרטים נשמרו");
      onClose();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="עריכת לקוח"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            שמירה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="שם הלקוח" required>
          {({ id }) => (
            <Input id={id} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <Field label="דוא״ל">
          {({ id }) => (
            <Input
              id={id}
              type="email"
              dir="ltr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="טלפון" help="בפורמט בינלאומי, לדוגמה +972541234567">
          {({ id }) => (
            <Input
              id={id}
              dir="ltr"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          )}
        </Field>
        <Field label="עיר">
          {({ id }) => (
            <Input id={id} value={city} onChange={(event) => setCity(event.target.value)} />
          )}
        </Field>
        {isManager && (
          <Field label="ריטיינר חודשי (₪)" help="מזין את חישוב הרווחיות">
            {({ id }) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={fee}
                onChange={(event) => setFee(event.target.value)}
              />
            )}
          </Field>
        )}
        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
